// src/AdminNumbersTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminNumbersTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchNumberAnalytics();
  }, []);

  const fetchNumberAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authedFetch(`${apiUrl('adminAnalytics')}?type=numbers`);
      const result = await response.json();
      
      if (result.success) {
        setAnalytics(result.data);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch number generation analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading number generation analytics...</div>;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>Error: {error}</p>
        <button onClick={fetchNumberAnalytics} style={refreshButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Number Generation & Assignment</h2>
        <div style={refreshContainerStyle}>
          <button onClick={fetchNumberAnalytics} style={refreshButtonStyle}>
            Refresh Data
          </button>
          {lastUpdated && (
            <span style={timestampStyle}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Generation Overview */}
      <div style={metricsGridStyle}>
        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Powerball Rollouts</h3>
          <div style={metricValueStyle}>{analytics.powerballRollouts.toLocaleString()}</div>
          <div style={metricSubtitleStyle}>Generated number sets</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Mega Millions Rollouts</h3>
          <div style={metricValueStyle}>{analytics.megamillionsRollouts.toLocaleString()}</div>
          <div style={metricSubtitleStyle}>Generated number sets</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Total Assignments</h3>
          <div style={metricValueStyle}>{(analytics.totalPBAssignments + analytics.totalMMAssignments).toLocaleString()}</div>
          <div style={metricSubtitleStyle}>Numbers assigned to members</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Unique Draw Dates</h3>
          <div style={metricValueStyle}>{analytics.uniqueDrawDates}</div>
          <div style={metricSubtitleStyle}>Different lottery dates</div>
        </div>
      </div>

      {/* Assignment Breakdown */}
      <div style={sectionStyle}>
        <h3>Assignment Breakdown by Game</h3>
        <div style={assignmentGridStyle}>
          <div style={assignmentCardStyle}>
            <div style={gameIconStyle}>🎲</div>
            <div style={gameLabelStyle}>Powerball</div>
            <div style={assignmentValueStyle}>{analytics.totalPBAssignments.toLocaleString()}</div>
            <div style={assignmentDescStyle}>Total assignments</div>
            <div style={avgStyle}>
              Avg: {analytics.avgPBNumbersPerMember} per active member
            </div>
          </div>
          <div style={assignmentCardStyle}>
            <div style={gameIconStyle}>💎</div>
            <div style={gameLabelStyle}>Mega Millions</div>
            <div style={assignmentValueStyle}>{analytics.totalMMAssignments.toLocaleString()}</div>
            <div style={assignmentDescStyle}>Total assignments</div>
            <div style={avgStyle}>
              Avg: {analytics.avgMMNumbersPerMember} per active member
            </div>
          </div>
        </div>
      </div>

      {/* Rollout Configuration */}
      <div style={sectionStyle}>
        <h3>Current Rollout Configuration</h3>
        <div style={configContainerStyle}>
          <div style={configItemStyle}>
            <span style={configLabelStyle}>Target Sets per Rollout:</span>
            <span style={configValueStyle}>{analytics.rolloutConfig.targetSets.toLocaleString()}</span>
          </div>
          <div style={configItemStyle}>
            <span style={configLabelStyle}>Last Updated:</span>
            <span style={configValueStyle}>
              {analytics.rolloutConfig.lastUpdated 
                ? new Date(analytics.rolloutConfig.lastUpdated).toLocaleString()
                : 'Not set'
              }
            </span>
          </div>
          <div style={configItemStyle}>
            <span style={configLabelStyle}>Updated By:</span>
            <span style={configValueStyle}>{analytics.rolloutConfig.updatedBy}</span>
          </div>
        </div>
      </div>

      {/* Assignment Activity */}
      <div style={sectionStyle}>
        <h3>Recent Assignment Activity</h3>
        <div style={activityContainerStyle}>
          <div style={activityItemStyle}>
            <div style={activityLabelStyle}>Latest Assignment Date</div>
            <div style={activityValueStyle}>
              {analytics.latestAssignmentDate || 'No assignments yet'}
            </div>
          </div>
          <div style={activityItemStyle}>
            <div style={activityLabelStyle}>Previous Assignment Date</div>
            <div style={activityValueStyle}>
              {analytics.previousAssignmentDate || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div style={sectionStyle}>
        <h3>System Health Summary</h3>
        <div style={healthContainerStyle}>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Generation Capacity:</span>
            <span style={healthValueStyle}>
              {analytics.powerballRollouts > 0 && analytics.megamillionsRollouts > 0 
                ? 'Both Games Active' 
                : 'Needs Attention'
              }
            </span>
          </div>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Assignment Coverage:</span>
            <span style={healthValueStyle}>
              {analytics.activeMemberCount > 0 && (analytics.totalPBAssignments + analytics.totalMMAssignments) > 0
                ? `${analytics.activeMemberCount} members covered`
                : 'No active assignments'
              }
            </span>
          </div>
          <div style={healthItemStyle}>
            <span style={healthLabelStyle}>Utilization Rate:</span>
            <span style={healthValueStyle}>
              {analytics.activeMemberCount > 0 
                ? `${Math.round(((analytics.totalPBAssignments + analytics.totalMMAssignments) / (analytics.activeMemberCount * 2)) * 100)}%`
                : '0%'
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
  paddingBottom: '1rem',
  borderBottom: '2px solid #e5e7eb'
};

const refreshContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const refreshButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
};

const timestampStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const metricsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginBottom: '3rem'
};

const metricCardStyle = {
  backgroundColor: 'white',
  padding: '1.5rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  textAlign: 'center'
};

const metricTitleStyle = {
  margin: '0 0 1rem 0',
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#666',
  textTransform: 'uppercase'
};

const metricValueStyle = {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#7c3aed'
};

const metricSubtitleStyle = {
  fontSize: '0.85rem',
  color: '#666',
  marginTop: '0.5rem'
};

const sectionStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const assignmentGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const assignmentCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px'
};

const gameIconStyle = {
  fontSize: '2rem',
  marginBottom: '0.5rem'
};

const gameLabelStyle = {
  fontSize: '1.2rem',
  fontWeight: '600',
  marginBottom: '0.5rem'
};

const assignmentValueStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#7c3aed',
  marginBottom: '0.5rem'
};

const assignmentDescStyle = {
  fontSize: '0.9rem',
  color: '#666',
  marginBottom: '0.5rem'
};

const avgStyle = {
  fontSize: '0.85rem',
  color: '#1976d2',
  fontWeight: '500'
};

const configContainerStyle = {
  marginTop: '1rem'
};

const configItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const configLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600'
};

const configValueStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#1976d2'
};

const activityContainerStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  marginTop: '1rem'
};

const activityItemStyle = {
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  textAlign: 'center'
};

const activityLabelStyle = {
  fontSize: '0.9rem',
  color: '#666',
  marginBottom: '0.5rem'
};

const activityValueStyle = {
  fontSize: '1.2rem',
  fontWeight: 'bold',
  color: '#7c3aed'
};

const healthContainerStyle = {
  marginTop: '1rem'
};

const healthItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const healthLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600'
};

const healthValueStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#1976d2'
};

const loadingStyle = {
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.2rem'
};

const errorStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#d32f2f'
};

export default AdminNumbersTab;